import {metadaApi} from '@dms/api/base';

import {
  GetSuppliersResponse,
  GetSuppliersResponseSchema,
  BaseSupplier,
  GetSuppliersRequest,
  GetSuppliersRequestSchema,
  GetSupplierResponse,
  GetSupplierRequest,
  GetSupplierRequestSchema,
  GetSupplierResponseSchema,
  GetSupplierDiscountGroupsResponse,
  GetSupplierDiscountGroupsRequest,
  GetSupplierDiscountGroupsRequestSchema,
  GetSupplierDiscountGroupsResponseSchema,
  PutRefreshSupplierDiscountGroupsResponse,
  PutRefreshSupplierDiscountGroupsRequest,
  PutRefreshSupplierDiscountGroupsRequestSchema,
  PutRefreshSupplierDiscountGroupsResponseSchema,
  GetSupplierDiscountGroupResponse,
  GetSupplierDiscountGroupRequest,
  GetSupplierDiscountGroupRequestSchema,
  GetSupplierDiscountGroupResponseSchema,
  PatchSupplierDiscountGroupResponse,
  PatchSupplierDiscountGroupRequest,
  PatchSupplierDiscountGroupRequestSchema,
  PatchSupplierDiscountGroupResponseSchema,
  BulkPatchSupplierDiscountGroupsResponse,
  BulkPatchSupplierDiscountGroupsRequest,
  BulkPatchSupplierDiscountGroupsRequestSchema,
  BulkPatchSupplierDiscountGroupsResponseSchema,
} from './types';

export const metadaWarehouseSupplierApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getSuppliers: build.query<BaseSupplier[], GetSuppliersRequest>({
      query: () => ({
        url: `warehouse/v1/supplier`,
      }),
      transformResponse: (response: GetSuppliersResponse) => response?.supplier ?? [],
      providesTags: ['suppliers'],
      extraOptions: {
        requestSchema: GetSuppliersRequestSchema,
        responseSchema: GetSuppliersResponseSchema,
      },
    }),
    getSupplier: build.query<GetSupplierResponse, GetSupplierRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/supplier/${queryArg.supplierId}`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [{type: 'suppliers', id: queryArg.supplierId}],
      extraOptions: {
        requestSchema: GetSupplierRequestSchema,
        responseSchema: GetSupplierResponseSchema,
      },
    }),
    getSupplierDiscountGroups: build.query<
      GetSupplierDiscountGroupsResponse,
      GetSupplierDiscountGroupsRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/supplier-discount-group',
        params: {
          supplierId: queryArg.supplierId,
          authorizationProfileId: queryArg.authorizationProfileId,
          discountGroupId: queryArg.discountGroupId,
        },
        queryArg,
      }),
      providesTags: ['supplierDiscountGroups'],
      extraOptions: {
        requestSchema: GetSupplierDiscountGroupsRequestSchema,
        responseSchema: GetSupplierDiscountGroupsResponseSchema,
      },
    }),
    refreshSupplierDiscountGroups: build.mutation<
      PutRefreshSupplierDiscountGroupsResponse,
      PutRefreshSupplierDiscountGroupsRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/supplier-discount-group/refresh',
        method: 'PUT',
        queryArg,
      }),
      extraOptions: {
        requestSchema: PutRefreshSupplierDiscountGroupsRequestSchema,
        responseSchema: PutRefreshSupplierDiscountGroupsResponseSchema,
      },
    }),
    getSupplierDiscountGroup: build.query<
      GetSupplierDiscountGroupResponse,
      GetSupplierDiscountGroupRequest
    >({
      query: (queryArg) => ({
        url: `warehouse/v1/supplier-discount-group/${queryArg.supplierDiscountGroupId}`,
        method: 'GET',
        queryArg,
      }),
      providesTags: ['supplierDiscountGroups'],
      extraOptions: {
        requestSchema: GetSupplierDiscountGroupRequestSchema,
        responseSchema: GetSupplierDiscountGroupResponseSchema,
      },
    }),
    patchSupplierDiscountGroup: build.mutation<
      PatchSupplierDiscountGroupResponse,
      PatchSupplierDiscountGroupRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/supplier-discount-group',
        method: 'PATCH',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: ['supplierDiscountGroups'],
      extraOptions: {
        requestSchema: PatchSupplierDiscountGroupRequestSchema,
        responseSchema: PatchSupplierDiscountGroupResponseSchema,
      },
    }),
    bulkPatchSupplierDiscountGroups: build.mutation<
      BulkPatchSupplierDiscountGroupsResponse,
      BulkPatchSupplierDiscountGroupsRequest
    >({
      query: (queryArg) => ({
        url: 'warehouse/v1/supplier-discount-group/bulk-edit',
        method: 'PATCH',
        body: queryArg,
        queryArg,
      }),
      invalidatesTags: ['supplierDiscountGroups'],
      extraOptions: {
        requestSchema: BulkPatchSupplierDiscountGroupsRequestSchema,
        responseSchema: BulkPatchSupplierDiscountGroupsResponseSchema,
      },
    }),
  }),
});

export const {
  useGetSuppliersQuery,
  useGetSupplierQuery,
  useGetSupplierDiscountGroupsQuery,
  useRefreshSupplierDiscountGroupsMutation,
  useGetSupplierDiscountGroupQuery,
  usePatchSupplierDiscountGroupMutation,
  useBulkPatchSupplierDiscountGroupsMutation,
} = metadaWarehouseSupplierApi;
